import translate from '../i18n'
import { Plan } from '../store/global/reducer'
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  TIME_FORMAT_SHORT,
  WECHAT_URL_LINK_EXPIRED_DAYS,
} from './constant'
import { UploadFile } from 'antd'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import * as _ from 'lodash'
import { URLSearchParams } from 'url'

dayjs.extend(relativeTime)

const momentFormat = (date: string | number, format: string) => {
  return dayjs(date).format(format)
}

export const getDateTime = (date: string | number) => {
  return momentFormat(date, DATE_TIME_FORMAT)
}

export const getDate = (date: string | number) => {
  return momentFormat(date, DATE_FORMAT)
}

export const getTime = (date: string | number) => {
  return momentFormat(date, TIME_FORMAT_SHORT)
}

export const getDayOfWeekLabel = (dayOfWeek: number) => {
  switch (dayOfWeek) {
    case 1:
      return translate('monday')
    case 2:
      return translate('tuesday')
    case 3:
      return translate('wednesday')
    case 4:
      return translate('thursday')
    case 5:
      return translate('friday')
    case 6:
      return translate('saturday')
    case 7:
      return translate('sunday')
  }
}

export const formatNumberToTime = (num: string | number) => {
  if (num == null) {
    return null
  }
  const number = Number(num) || 0
  const str = number.toString().padStart(4, '0')
  return `${str.slice(0, 2)}:${str.slice(2)}`
}

export const getWeChatURLLinkIsExpiredTag = (
  createdAt: string | number,
  expiredDays = WECHAT_URL_LINK_EXPIRED_DAYS,
) => {
  const expiredAt = dayjs(createdAt).add(expiredDays, 'days')
  const isExpired = dayjs().isAfter(expiredAt)
  return isExpired ? 'Expired' : `Expires ${expiredAt.fromNow()}`
}

export const getDiscountOff = (plans: Plan[], planId: number, price: number) => {
  const originPrice = plans.find(item => item.id === planId)?.price
  if (!originPrice) {
    return 0
  }
  return (((price * 100) / originPrice) * 10).toFixed(1)
}

export const getLowestDiscountOff = (
  plans: Plan[],
  percentageOffPlans: { planId: number; price: number }[],
) => {
  const percentageOffPlansWithDiscountOff = percentageOffPlans.map(
    (item: { planId: number; price: number }) => {
      return {
        ...item,
        discountOff: getDiscountOff(plans, item.planId, item.price),
      }
    },
  )
  const lowestDiscountOff = _.sortBy(percentageOffPlansWithDiscountOff, 'discountOff')[0]
    .discountOff
  return {
    lowestDiscountOff,
    percentageOffPlansWithDiscountOff,
  }
}

export const getOptions = (
  array: { id: number; name: string }[],
): { label: string; value: number }[] => {
  const options = array.map(item => {
    if (item.id <= 0) {
      return {
        label: item.name,
        value: item.id,
      }
    } else {
      return {
        label: `${item.id}-${item.name}`,
        value: item.id,
      }
    }
  })
  return options
}

export const getSearchValues = (
  keys: { key: string; isBoolean: boolean; isArray?: boolean; default?: any }[],
  searchParams: URLSearchParams,
) => {
  const searchValues: { [key: string]: any } = {}
  keys.forEach(element => {
    let value = undefined
    if (element.isArray) {
      value = searchParams.getAll(element.key)
    } else {
      value = searchParams.get(element.key)
    }
    const defaultValue = element.default ?? undefined

    if (element.isBoolean) {
      searchValues[element.key] = value === null ? defaultValue : value === 'true'
    } else {
      searchValues[element.key] = value ?? defaultValue
    }
  })

  return searchValues
}

export const getImageUrl = (filename: string, path: string, files?: UploadFile[]) => {
  if (!files || files.length === 0) {
    return ''
  }

  const file = files[0]
  if (_.hasIn(file, 'url')) {
    return file.url ?? ''
  } else {
    const fileExtension = file?.name.split('.').pop()
    return `${path}/${filename}_${file.uid}.${fileExtension}`
  }
}

export const getSearchParams = (data: any) => {
  return _.pickBy(data, item => item !== undefined && item !== null)
}

export const renderNullableValue = (
  value: number | null,
  formatter: (val: number) => string = String,
): string => {
  if (value === null) {
    return 'N/A'
  }
  return formatter(value)
}
